/**
 * Decides whether the person on this page may use Intercom chat, and starts or stops the Messenger
 * to match. Some partner programs (New York Life) keep their clients out of chat entirely.
 *
 * `useSupportChatAllowed` answers `null` only while the session is resolving, which leaves the
 * Messenger as it is: nothing has started on a first load, and an advisor's open chat is not torn
 * down while their session reloads. Every other unknown answer is `false`, which also stops a
 * Messenger an earlier page started.
 * - Signed in as an advisor or wizard: the server's answer, `session.supportChatWithheld`.
 * - Signed in as a client: the server's answer, and on the reusable intake link page also the
 *   advisor's, since the client is about to start an assessment with them.
 * - Signed in to Clerk without a usable session (the server failed, or the account is a
 *   duplicate): open only if this browser remembers the last session as allowed.
 * - Signed out: closed on the intake-link hand-off page, which leaves as soon as its lookup says
 *   which program the client belongs to; closed on the agent-entered consent page, which only a
 *   withholding program's customers reach; closed when the last person this browser identified
 *   was a withheld client (`rememberSupportChatWithheld`); and on the reusable intake link page,
 *   also closed until the advisor's public profile says their program allows chat
 *   (`useAdvisorPublicProfileQuery`, shared with the page).
 *
 * The browser remembers an answer only from the people it describes: a signed-in session's own
 * answer, and for a signed-out visitor on the reusable link, an advisor answer that keeps chat
 * closed (including one that could not be read). An advisor previewing that link leaves it alone.
 */

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { AGENT_ENTERED_CONSENT_PATH, SessionType } from '@shared';
import { selectSession } from '@client/model';
import { useAdvisorPublicProfileQuery } from '@client/hooks/useAdvisorPublicProfile';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { isPublicOnboardingPath, ROUTE_PATTERNS } from '@client/hooks/useClientRoutes';
import {
  initializeIntercom,
  isSupportChatRememberedAsAllowed,
  isSupportChatRememberedAsWithheld,
  rememberSupportChatWithheld,
  shutdownIntercom,
} from '@client/util/intercomUtil';

const SIGNED_IN_SESSION_TYPES = [SessionType.advisor, SessionType.client, SessionType.wizard];

const PUBLIC_INVITE_ROUTE = `${ROUTE_PATTERNS.PUBLIC_ONBOARD}:advisorId`;

/**
 * `undefined` off the reusable intake link page. On it: `null` until the advisor's profile
 * answers, then whether their program withholds chat. A failed lookup, or an answer without the
 * field, counts as withheld.
 */
function usePublicInviteSupportChatWithheld(): boolean | null | undefined {
  const { pathname } = useLocation();
  const advisorId = matchPath(PUBLIC_INVITE_ROUTE, pathname)?.params.advisorId;
  const profile = useAdvisorPublicProfileQuery(advisorId);

  if (!advisorId) {
    return undefined;
  }
  if (profile.isPending) {
    return null;
  }
  return profile.data?.supportChatWithheld !== false;
}

export function useSupportChatAllowed(): boolean | null {
  const { pathname } = useLocation();
  const { sessionType, isLoading, supportChatWithheld } = useSelector(selectSession);
  const { isClerkSignedIn } = useClerkClientAuth();
  const publicInviteWithheld = usePublicInviteSupportChatWithheld();

  if (sessionType === SessionType.unknown || isLoading) {
    return null;
  }

  const publicInviteAllowsChat =
    publicInviteWithheld === undefined ? null : publicInviteWithheld === false;

  if (sessionType === SessionType.client) {
    return !supportChatWithheld && publicInviteAllowsChat !== false;
  }

  if (SIGNED_IN_SESSION_TYPES.includes(sessionType)) {
    return !supportChatWithheld;
  }

  if (isPublicOnboardingPath(pathname) || pathname.startsWith(AGENT_ENTERED_CONSENT_PATH)) {
    return false;
  }

  const rememberedAllowsChat = isClerkSignedIn
    ? isSupportChatRememberedAsAllowed()
    : !isSupportChatRememberedAsWithheld();

  return publicInviteAllowsChat === null
    ? rememberedAllowsChat
    : publicInviteAllowsChat && rememberedAllowsChat;
}

export function useSupportChatMessenger(): boolean | null {
  const isAllowed = useSupportChatAllowed();
  const { sessionType, supportChatWithheld } = useSelector(selectSession);
  const { isClerkSignedIn } = useClerkClientAuth();
  const publicInviteWithheld = usePublicInviteSupportChatWithheld();
  const isSignedIn = SIGNED_IN_SESSION_TYPES.includes(sessionType);
  const isSignedOutOnWithheldPublicInvite =
    sessionType === SessionType.unauthenticated &&
    !isClerkSignedIn &&
    publicInviteWithheld === true;

  useEffect(() => {
    if (isAllowed === true) {
      initializeIntercom();
    } else if (isAllowed === false) {
      shutdownIntercom();
    }
  }, [isAllowed]);

  useEffect(() => {
    if (isSignedIn) {
      rememberSupportChatWithheld(supportChatWithheld);
    }
  }, [isSignedIn, supportChatWithheld]);

  useEffect(() => {
    if (isSignedOutOnWithheldPublicInvite) {
      rememberSupportChatWithheld(true);
    }
  }, [isSignedOutOnWithheldPublicInvite]);

  return isAllowed;
}
